import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, MessageCircle, ChevronRight } from 'lucide-react';
import { ROOM_THEMES, ROOM_ORDER } from '@/config/roomThemes';
import { getStaticMindsPreview } from '@/config/staticMuseumData';
import FeatureBadge from '@/components/FeatureBadge';

/* ──────────────────────────────────
   3D Museum Hallway — Game-like corridor
   ────────────────────────────────── */
const MuseumHallway = ({ rooms = [], onSelectRoom, onRequestRoom, isLoading }) => {
  const [hoveredDoor, setHoveredDoor] = useState(null);
  const [mindsCache, setMindsCache] = useState({});
  const [mousePos, setMousePos] = useState({ x: 0.5, y: 0.5 });
  const [enteringRoom, setEnteringRoom] = useState(null);
  const containerRef = useRef(null);

  const handleMouseMove = useCallback((e) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    setMousePos({
      x: (e.clientX - rect.left) / rect.width,
      y: (e.clientY - rect.top) / rect.height,
    });
  }, []);

  const themedRooms = ROOM_ORDER
    .map(id => {
      const room = rooms.find(r => r.id === id);
      return room ? { ...room, theme: ROOM_THEMES[id] } : null;
    })
    .filter(Boolean);

  const fetchMindsPreview = async (roomId) => {
    if (mindsCache[roomId]) return;
    setMindsCache(prev => ({ ...prev, [roomId]: getStaticMindsPreview(roomId) }));
    if (!process.env.REACT_APP_BACKEND_URL) return;

    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/rooms/${roomId}/minds`);
      if (!res.ok) throw new Error(`Preview request failed: ${res.status}`);
      const data = await res.json();
      setMindsCache(prev => ({ ...prev, [roomId]: data.slice(0, 3) }));
    } catch (e) { /* static preview remains available */ }
  };

  const handleDoorClick = (room) => {
    setEnteringRoom(room.id);
    setTimeout(() => onSelectRoom(room), 600);
  };

  // Dynamic perspective based on mouse
  const perspectiveOriginX = 40 + mousePos.x * 20; // 40-60%
  const perspectiveOriginY = 35 + mousePos.y * 30; // 35-65%
  const tiltX = (mousePos.y - 0.5) * -3; // subtle tilt
  const tiltY = (mousePos.x - 0.5) * 4;

  return (
    <div
      ref={containerRef}
      data-testid="museum-hallway"
      className="relative min-h-screen overflow-hidden"
      style={{ background: '#FDFCFB' }}
      onMouseMove={handleMouseMove}
    >
      {/* Marble floor reflection */}
      <div className="absolute inset-0"
        style={{
          background: 'linear-gradient(180deg, #FDFCFB 0%, #F2EFE9 60%, #E8E3DC 100%)',
        }}
      />

      {/* Floor tiles pattern */}
      <div className="absolute bottom-0 left-0 right-0 h-[35%]"
        style={{
          background: `
            repeating-conic-gradient(#E6E1DB 0% 25%, #F2EFE9 0% 50%) 0 0 / 80px 80px,
            linear-gradient(180deg, transparent 0%, rgba(242,239,233,0.8) 100%)
          `,
          opacity: 0.4,
          transform: `perspective(600px) rotateX(60deg)`,
          transformOrigin: 'bottom center',
        }}
      />

      {/* Ceiling detail */}
      <div className="absolute top-0 left-0 right-0 h-16"
        style={{ background: 'linear-gradient(to bottom, #E6E1DB 0%, transparent 100%)', opacity: 0.3 }}
      />

      {/* Ambient warm light from above */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[500px]"
        style={{
          background: 'radial-gradient(ellipse, rgba(184,134,11,0.06) 0%, transparent 60%)',
          transform: `translate(${(mousePos.x - 0.5) * 20}px, 0)`,
          transition: 'transform 0.5s ease-out',
        }}
      />

      <div className="paper-texture" />

      {/* Header */}
      <motion.div
        className="relative z-10 text-center pt-10 sm:pt-14 pb-4 sm:pb-6 px-4"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.15 }}
      >
        <p className="font-ui text-[10px] sm:text-xs tracking-[0.4em] uppercase mb-2" style={{ color: '#B8860B' }}>
          Choose Your Hall
        </p>
        <h2 className="font-display text-3xl sm:text-4xl md:text-5xl tracking-wide" style={{ color: '#1A1512' }}>
          The Grand Halls
        </h2>
        <motion.div
          className="h-px mx-auto mt-4"
          style={{ background: 'linear-gradient(to right, transparent, #B8860B, transparent)', opacity: 0.35 }}
          initial={{ width: 0 }}
          animate={{ width: 120 }}
          transition={{ duration: 1, delay: 0.4 }}
        />
      </motion.div>

      {/* 3D Perspective Container */}
      <div
        className="relative z-10 flex items-center justify-center px-4 sm:px-8"
        style={{
          perspective: '1400px',
          perspectiveOrigin: `${perspectiveOriginX}% ${perspectiveOriginY}%`,
          minHeight: 'calc(100vh - 260px)',
        }}
      >
        <motion.div
          className="grid grid-cols-2 sm:grid-cols-3 gap-6 sm:gap-8 lg:gap-10 items-end w-full max-w-6xl"
          style={{
            transformStyle: 'preserve-3d',
            transform: `rotateX(${tiltX}deg) rotateY(${tiltY}deg)`,
            transition: 'transform 0.4s ease-out',
          }}
          initial={{ opacity: 0, rotateX: 5 }}
          animate={{ opacity: 1, rotateX: tiltX }}
        >
          {themedRooms.map((room, index) => (
            <Door3D
              key={room.id}
              room={room}
              theme={room.theme}
              index={index}
              total={themedRooms.length}
              isHovered={hoveredDoor === room.id}
              isEntering={enteringRoom === room.id}
              mindsPreview={mindsCache[room.id] || []}
              mousePos={mousePos}
              onHover={() => { setHoveredDoor(room.id); fetchMindsPreview(room.id); }}
              onLeave={() => setHoveredDoor(null)}
              onClick={() => handleDoorClick(room)}
            />
          ))}
        </motion.div>
      </div>

      {/* Custom room CTA */}
      <motion.div className="relative z-10 text-center pb-8" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.2 }}>
        <button
          data-testid="request-room-btn"
          onClick={onRequestRoom}
          className="inline-flex items-center gap-2 px-6 py-3 border border-dashed font-ui text-xs tracking-widest uppercase cursor-pointer transition-all duration-300"
          style={{ borderColor: '#E6E1DB', color: '#8C8680' }}
          onMouseEnter={e => { e.currentTarget.style.borderColor = '#B8860B'; e.currentTarget.style.color = '#B8860B'; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = '#E6E1DB'; e.currentTarget.style.color = '#8C8680'; }}
        >
          <Plus className="w-3 h-3" />
          <span>Request a Custom Hall</span>
          <FeatureBadge feature="customHallRequest" compact />
        </button>
      </motion.div>
    </div>
  );
};

/* ──────────────────────────────────
   3D Door Component
   ────────────────────────────────── */
const Door3D = ({ room, theme, index, total, isHovered, isEntering, mindsPreview, mousePos, onHover, onLeave, onClick }) => {
  // Each door at different Z depth for corridor feel
  const zOffset = (index - (total - 1) / 2) * -20;
  const xShift = (index - (total - 1) / 2) * 2;

  return (
    <motion.div
      data-testid={`room-door-${room.id}`}
      className="relative cursor-pointer flex flex-col items-center"
      initial={{ opacity: 0, y: 50, z: -100 }}
      animate={{
        opacity: isEntering ? 0 : 1,
        y: 0,
        z: 0,
        scale: isEntering ? 2.5 : 1,
      }}
      transition={{
        duration: isEntering ? 0.6 : 0.7,
        delay: isEntering ? 0 : 0.25 + index * 0.12,
        ease: isEntering ? [0.45, 0, 0.55, 1] : [0.16, 1, 0.3, 1],
      }}
      onMouseEnter={onHover}
      onMouseLeave={onLeave}
      onClick={onClick}
      style={{
        transformStyle: 'preserve-3d',
        transform: `translateZ(${zOffset}px) translateX(${xShift}px)`,
      }}
    >
      {/* Door arch frame (outer) */}
      <motion.div
        className="relative overflow-hidden"
        style={{
          width: 'clamp(180px, 18vw, 250px)',
          aspectRatio: '2.8/5',
          borderRadius: '50% 50% 4px 4px / 30% 30% 0 0',
          background: '#fff',
          border: `3px solid ${isHovered ? theme.accent : '#E6E1DB'}`,
          boxShadow: isHovered
            ? `0 30px 60px -10px rgba(0,0,0,0.15), 0 0 0 1px ${theme.accent}20, 0 0 40px ${theme.accent}10`
            : '0 20px 40px -10px rgba(0,0,0,0.08), 0 0 0 1px rgba(230,225,219,0.5)',
          transition: 'all 0.5s cubic-bezier(0.22, 1, 0.36, 1)',
          transformStyle: 'preserve-3d',
        }}
        animate={{
          rotateY: isHovered ? -8 : 0,
          translateZ: isHovered ? 30 : 0,
          y: isHovered ? -12 : 0,
        }}
        transition={{ type: 'spring', stiffness: 200, damping: 25 }}
      >
        {/* Door background gradient */}
        <div className="absolute inset-0" style={{ background: theme.doorGradient }} />

        {/* Frosted glass effect inside door */}
        <div className="absolute inset-0" style={{
          background: `radial-gradient(ellipse at 50% 40%, ${theme.accent}12 0%, transparent 50%)`,
        }} />

        {/* Door panel details */}
        <div className="absolute inset-[12px] sm:inset-[16px] rounded-t-[45%]"
          style={{
            border: `1px solid ${isHovered ? theme.accent + '30' : '#E6E1DB'}`,
            transition: 'border-color 0.5s ease',
          }}
        >
          {/* Vertical split */}
          <div className="absolute top-[15%] bottom-0 left-1/2 w-px" style={{ background: `${theme.accent}15` }} />

          {/* Keystone arch ornament */}
          <div className="absolute top-[8%] left-1/2 -translate-x-1/2">
            <div className="w-3 h-3 rotate-45" style={{ background: `${theme.accent}25`, border: `1px solid ${theme.accent}15` }} />
          </div>

          {/* Door handles with 3D depth */}
          <div className="absolute top-[55%] left-1/2 -translate-x-1/2 flex gap-4">
            <motion.div
              className="w-1.5 h-7 rounded-full"
              style={{ background: isHovered ? theme.accent : `${theme.accent}30`, transition: 'background 0.3s' }}
              animate={{ scaleY: isHovered ? 1.1 : 1 }}
            />
            <motion.div
              className="w-1.5 h-7 rounded-full"
              style={{ background: isHovered ? theme.accent : `${theme.accent}30`, transition: 'background 0.3s' }}
              animate={{ scaleY: isHovered ? 1.1 : 1 }}
            />
          </div>

          {/* Side panel ornaments */}
          <div className="absolute top-[25%] left-2 w-1 h-[30%] rounded-full" style={{ background: `${theme.accent}08` }} />
          <div className="absolute top-[25%] right-2 w-1 h-[30%] rounded-full" style={{ background: `${theme.accent}08` }} />
        </div>

        {/* Light spill from behind door */}
        <motion.div
          className="absolute bottom-0 left-0 right-0 h-24"
          style={{ background: `linear-gradient(to top, ${theme.accent}12, transparent)` }}
          animate={{ opacity: isHovered ? 1 : 0.3 }}
          transition={{ duration: 0.4 }}
        />

        {/* Hover: Minds preview at bottom */}
        <AnimatePresence>
          {isHovered && mindsPreview.length > 0 && (
            <motion.div
              className="absolute bottom-4 left-0 right-0 flex justify-center gap-1.5"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 8 }}
              transition={{ duration: 0.25 }}
            >
              {mindsPreview.map(m => (
                <div key={m.id} className="w-9 h-9 rounded-full overflow-hidden shadow-lg ring-2" style={{ ringColor: `${theme.accent}30` }}>
                  {m.portrait ? <img src={m.portrait} alt={m.name} className="w-full h-full object-cover" loading="lazy" /> : <div className="w-full h-full flex items-center justify-center text-[9px] font-bold" style={{ background: theme.surface, color: theme.accent }}>{m.name[0]}</div>}
                </div>
              ))}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Enter arrow on hover */}
        <AnimatePresence>
          {isHovered && (
            <motion.div
              className="absolute top-[38%] left-1/2 -translate-x-1/2 flex flex-col items-center gap-1"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: theme.accent, boxShadow: `0 4px 15px ${theme.accent}40` }}>
                <ChevronRight className="w-5 h-5 text-white" />
              </div>
              <span className="font-ui text-[9px] tracking-wider uppercase" style={{ color: theme.accent }}>Enter</span>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>

      {/* Name plaque — styled like a museum label */}
      <motion.div
        className="text-center mt-5 sm:mt-6 px-4"
        animate={{ y: isHovered ? -4 : 0 }}
        transition={{ duration: 0.3 }}
      >
        <div className="inline-block px-5 py-2 rounded-sm" style={{
          background: isHovered ? 'white' : 'transparent',
          boxShadow: isHovered ? '0 4px 16px rgba(0,0,0,0.06)' : 'none',
          border: isHovered ? '1px solid #E6E1DB' : '1px solid transparent',
          transition: 'all 0.4s ease',
        }}>
          <h3 className="font-display text-base sm:text-lg md:text-xl tracking-wide transition-colors duration-400"
            style={{ color: isHovered ? theme.accent : '#1A1512' }}>
            {theme.name}
          </h3>
          <p className="font-body text-[11px] sm:text-xs mt-0.5" style={{ color: '#8C8680' }}>
            &ldquo;{theme.tagline}&rdquo;
          </p>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default MuseumHallway;
